import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
  IconButton,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import { Person, CalendarToday, CheckCircle } from '@mui/icons-material';
import { RefundSummary } from '../../../../types/sales.types';
import { useReturnConfirmationModal } from './ReturnConfirmationModal.hooks';
import {
  InfoGrid,
  InfoHeader,
  SummaryBox,
  SummaryRow,
  TotalRow,
  ConfirmButton,
} from './ReturnConfirmationModal.styles';
import {
  DialogZoomTransition,
  DIALOG_ZOOM_MS,
  dialogZoomSlotProps,
  dialogZoomDialogSx,
  dialogZoomPaperSx,
} from '../../../ui/DialogZoomTransition';
import { colors } from '../../../../styles/theme';

export interface ReturnConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  refundSummary: RefundSummary | null;
}

const ReturnConfirmationModal: React.FC<ReturnConfirmationModalProps> = ({
  open,
  onClose,
  refundSummary,
}) => {
  const {
    formattedReturnDate,
    formattedOriginalTotal,
    formattedRestockingFee,
    formattedRefundAmount,
    restockingFeeLabel,
    displayUserName,
    hasRestockingFee,
    formattedBreakdown,
  } = useReturnConfirmationModal({ refundSummary });

  if (!refundSummary) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={DialogZoomTransition}
      transitionDuration={DIALOG_ZOOM_MS}
      slotProps={dialogZoomSlotProps}
      sx={dialogZoomDialogSx}
      PaperProps={{
        sx: (theme) =>
          dialogZoomPaperSx({
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 20px 60px rgba(0, 0, 0, 0.55)'
                : '0 20px 60px rgba(2, 6, 23, 0.18)',
            maxHeight: '90vh',
            margin: 'auto',
          }),
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          m: 0,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            px: 3,
            pt: 3,
            pb: 2,
            background: `linear-gradient(135deg, ${colors.success.dark} 0%, ${colors.info.dark} 100%)`,
            color: '#fff',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'rgba(255,255,255,0.9)',
              '&:hover': {
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.12)',
              },
            }}
            aria-label="Cerrar"
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              pr: 5,
            }}
          >
            <CheckCircle sx={{ fontSize: 36, opacity: 0.95 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                Devolución procesada
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                El reembolso quedó registrado correctamente
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={(theme) => ({
          pt: 3,
          pb: 2,
          px: 3,
          backgroundColor: theme.palette.background.default,
        })}
      >
        <InfoGrid>
          <div>
            <InfoHeader>
              <CalendarToday sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="500"
                textTransform="uppercase"
                letterSpacing="0.5px"
              >
                Fecha
              </Typography>
            </InfoHeader>
            <Typography variant="body1" fontWeight="500" color="text.primary">
              {formattedReturnDate}
            </Typography>
          </div>

          <div>
            <InfoHeader>
              <Person sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="500"
                textTransform="uppercase"
                letterSpacing="0.5px"
              >
                Usuario
              </Typography>
            </InfoHeader>
            <Typography variant="body1" fontWeight="500" color="text.primary">
              {displayUserName}
            </Typography>
          </div>
        </InfoGrid>

        <Divider sx={{ my: 2.5 }} />

        <SummaryBox>
          <Typography
            variant="subtitle1"
            fontWeight="600"
            mb={2.5}
            textAlign="center"
            letterSpacing="0.5px"
            sx={(theme) => ({
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.success.light
                  : theme.palette.success.dark,
            })}
          >
            Resumen de reembolso
          </Typography>

          <SummaryRow>
            <Typography variant="body2" color="text.secondary">
              Total original
            </Typography>
            <Typography variant="body1" fontWeight="500" color="text.primary">
              {formattedOriginalTotal}
            </Typography>
          </SummaryRow>

          {hasRestockingFee && (
            <SummaryRow>
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.warning.light
                      : theme.palette.warning.dark,
                })}
              >
                {restockingFeeLabel}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="500"
                sx={(theme) => ({
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.warning.light
                      : theme.palette.warning.dark,
                })}
              >
                -{formattedRestockingFee}
              </Typography>
            </SummaryRow>
          )}

          <Divider
            sx={(theme) => ({
              my: 2,
              backgroundColor:
                theme.palette.mode === 'dark' ? alpha(theme.palette.success.main, 0.35) : '#bbf7d0',
            })}
          />

          <TotalRow>
            <Typography
              variant="subtitle2"
              fontWeight="600"
              sx={(theme) => ({
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.success.light
                    : theme.palette.success.dark,
              })}
            >
              Total a reembolsar
            </Typography>
            <Typography
              variant="h6"
              fontWeight="700"
              sx={(theme) => ({
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.success.light
                    : theme.palette.success.dark,
              })}
            >
              {formattedRefundAmount}
            </Typography>
          </TotalRow>
        </SummaryBox>

        {formattedBreakdown.length > 0 && (
          <>
            <Typography
              variant="subtitle2"
              sx={{
                mt: 3,
                mb: 1.5,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                fontSize: '0.72rem',
                color: 'text.secondary',
              }}
            >
              Detalle por ítem
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                mb: 1,
                backgroundImage: 'none',
              }}
            >
              <Table size="small" sx={{ minWidth: 500 }}>
                <TableHead>
                  <TableRow
                    sx={(theme) => ({
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.04)
                          : theme.palette.grey[50],
                    })}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Precio
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Reabast.
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Reembolso
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formattedBreakdown.map((row) => (
                    <TableRow key={row.itemId} hover>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" noWrap title={row.productName}>
                          {row.productName}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{row.originalPriceFormatted}</TableCell>
                      <TableCell
                        align="right"
                        sx={(theme) => ({
                          color:
                            row.restockingFee > 0
                              ? theme.palette.mode === 'dark'
                                ? theme.palette.warning.light
                                : theme.palette.warning.dark
                              : theme.palette.text.secondary,
                        })}
                      >
                        {row.restockingFee > 0 ? `-${row.restockingFeeFormatted}` : '—'}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={(theme) => ({
                          fontWeight: 600,
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.success.light
                              : theme.palette.success.dark,
                        })}
                      >
                        {row.refundAmountFormatted}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={(theme) => ({
          px: 3,
          pb: 3,
          pt: 1,
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <ConfirmButton onClick={onClose} variant="contained" fullWidth>
          Salir
        </ConfirmButton>
      </DialogActions>
    </Dialog>
  );
};

export default ReturnConfirmationModal;
